/**
 * Header màn "Contract Detail" — mirror Access `frmProcurementMainSubFormDetailsP` /
 * `…DetailsNPE` (Single Form, RecordSource *_pick 1 row):
 *
 * Hàng 1 có CẢ HAI picker (Access để 2 combo cạnh nhau trên cùng header):
 *   [P Contract ▼] [NPE Contract ▼] [Delivery (P) | Certification (NPE)]
 * Chọn combo nào → chuyển variant đó (Access đổi SourceObject navProcurement).
 *
 * Field value read-only theo grid 3 cột × 8 hàng Access (gridColumn/gridRow explicit
 * ở md — skill /convert-form); mobile stack 1 cột. Số Format "Standard" (2dp, căn phải),
 * Percent, date DD-MMM-YYYY. Field "To be *" chữ ĐỎ (Access ForeColor 255).
 */
import { Autocomplete, Box, Card, CardContent, FormControlLabel, Radio, RadioGroup, TextField, Typography } from '@mui/material'
import { alpha, type Theme } from '@mui/material/styles'
import { useMemo } from 'react'

import type { ContractPicks, HeaderNPE, HeaderP } from '../../store/api/procurementApi'
import { COMBOBOX_FILTER_COMPACT } from '../../theme/responsive'

export type ContractVariant = 'p' | 'npe'

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

const fmtDate = (v: unknown): string => {
  if (!v) return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${d.getFullYear()}`
}

// Access Format "Standard" = #,##0.00;(#,##0.00) — số âm bọc ngoặc.
const fmtStd = (v: unknown): string => {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return ''
  const s = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return n < 0 ? `(${s})` : s
}

const fmtPercent = (v: unknown): string => {
  if (v == null || v === '') return ''
  const n = Number(v)
  if (!Number.isFinite(n)) return ''
  const s = `${(Math.abs(n) * 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%`
  return n < 0 ? `(${s})` : s
}

// Số âm (đã bọc ngoặc bởi fmtStd/fmtPercent) → tô đỏ.
const isNegative = (t: string): boolean => /^\(.*\d.*\)$/.test(t)

interface FieldPart {
  text: string
  align?: 'left' | 'right'
  red?: boolean
  bold?: boolean
}

/** 1 ô field của header: label trái + 1..2 value box phải (Grade/Cert, Type/Date). */
function HField({
  label,
  parts,
  gridColumn,
  gridRow,
  multiline = false,
}: {
  label: string
  parts: FieldPart[]
  gridColumn?: { md?: string; lg?: string }
  gridRow?: { md?: string; lg?: string }
  multiline?: boolean
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: multiline ? 'flex-start' : 'center',
        alignSelf: multiline ? 'stretch' : undefined,
        gap: { xs: 0.75, lg: 0.25 },
        minWidth: 0,
        // md auto-flow (bỏ đặt cứng cũ vì hàng đầu giờ có 3 combobox); chỉ giữ lg override.
        gridColumn: { xs: 'auto', md: 'auto', lg: gridColumn?.lg ?? 'auto' },
        gridRow: { xs: 'auto', md: 'auto', lg: gridRow?.lg ?? 'auto' },
      }}
    >
      <Typography
        sx={{ fontSize: { xs: '0.72rem', lg: '0.66rem' }, color: 'text.secondary', width: { xs: 118, lg: 84 }, flexShrink: 0, lineHeight: 1.2, whiteSpace: 'pre-line' }}
      >
        {label.replace(/[^\S\n]+/g, '')}
      </Typography>
      <Box sx={{ display: 'flex', gap: 0.5, flex: 1, minWidth: 0, alignSelf: 'stretch' }}>
        {parts.map((p, i) => (
          <Box
            key={i}
            sx={{
              flex: 1,
              minWidth: 0,
              px: 0.75,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 0.5,
              bgcolor: 'common.white',
              fontSize: { xs: '0.76rem', lg: '0.68rem' },
              textAlign: p.align ?? 'left',
              color: p.red || isNegative(p.text) ? 'error.main' : 'text.primary',
              fontWeight: p.bold || p.red ? 600 : 400,
              ...(multiline
                ? { minHeight: FIELD_H, py: 0.25, lineHeight: 1.3, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', overflow: 'auto' }
                : { height: FIELD_H, py: 0, lineHeight: FIELD_LINE, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
            }}
            title={multiline ? undefined : p.text || undefined}
          >
            {p.text || ' '}
          </Box>
        ))}
      </Box>
    </Box>
  )
}

interface PickOption {
  value: string
  label: string
}

interface Props {
  variant: ContractVariant
  picks: ContractPicks | undefined
  picksLoading: boolean
  pContractNo: string | null
  npeContractNo: string | null
  onPickP: (contractNo: string | null) => void
  onPickNPE: (contractNo: string | null) => void
  onSelectType: (variant: ContractVariant) => void
  /** Nav view (Pay Info / Advance / Fixation) — combobox sau Contract List. */
  mode: string
  modeOptions: PickOption[]
  onModeChange: (mode: string) => void
  headerP: HeaderP | null | undefined
  headerNPE: HeaderNPE | null | undefined
}

/** Chiều cao đồng nhất cho MỌI field textbox + combobox header (border-box). */
const FIELD_H = { xs: 28, lg: 24 }
/** line-height = content-box height (trừ 2px border) để căn giữa dọc 1 dòng. */
const FIELD_LINE = { xs: '26px', lg: '22px' }

/** Contract Type combo — gộp 2 picker cũ (Access AfterUpdate đổi SourceObject). */
const TYPE_OPTIONS: PickOption[] = [
  { value: 'p', label: 'Purchase' },
  { value: 'npe', label: 'NPE' },
]

/** sx dùng chung cho combobox header — format NỔI BẬT (tint + viền primary) để focus. */
const pickerSx = (theme: Theme) => ({
  // Cast to object: COMBOBOX_FILTER_COMPACT is typed SxProps<Theme> (a union that
  // can be an array/fn) — spreading it raw poisons the literal's inferred type.
  ...(COMBOBOX_FILTER_COMPACT as object),
  flex: 1,
  minWidth: 0,
  // Copy format field bên dưới: nền trắng, viền divider mảnh, radius 0.5.
  // Nhắm 2 class (.MuiInputBase-root.MuiOutlinedInput-root) để specificity > theme
  // MuiTextField override (ép bg trắng + radius 10 + hover/focus primary) — kẻo revert style cũ.
  // Copy format field bên dưới: nền trắng, viền divider mảnh, radius 0.5.
  // KHÁC BIỆT DUY NHẤT: màu chữ primary + bold. Nhắm 2 class để specificity > theme.
  '& .MuiInputBase-root.MuiOutlinedInput-root': {
    height: FIELD_H,
    minHeight: 0,
    py: 0,
    borderRadius: 0.5,
    fontSize: { xs: '0.72rem', lg: '0.68rem' },
    bgcolor: theme.palette.common.white,
  },
  '& .MuiInputBase-input': {
    py: 0,
    fontSize: { xs: '0.72rem', lg: '0.68rem' },
    fontWeight: 700,
    color: theme.palette.primary.main,
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: theme.palette.divider, borderWidth: 1 },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: theme.palette.divider,
    borderWidth: 1,
  },
  '& .MuiAutocomplete-endAdornment': { top: '50%', transform: 'translateY(-50%)' },
})

export default function ContractDetailHeader({
  variant,
  picks,
  picksLoading,
  pContractNo,
  npeContractNo,
  onPickP,
  onPickNPE,
  onSelectType,
  mode,
  modeOptions,
  onModeChange,
  headerP,
  headerNPE,
}: Props) {
  const typeSelected = TYPE_OPTIONS.find((o) => o.value === variant) ?? TYPE_OPTIONS[1]
  const modeSelected = modeOptions.find((o) => o.value === mode) ?? modeOptions[0] ?? null
  // Option label = contract_no (dòng chính); metadata (supplier · date · ref) render
  // ở dòng phụ trong dropdown — copy format combobox PO No của BatchReport.
  const pOptions = useMemo<PickOption[]>(
    () => (picks?.p ?? []).map((it) => ({ value: it.contract_no, label: it.contract_no })),
    [picks?.p],
  )
  const npeOptions = useMemo<PickOption[]>(
    () => (picks?.npe ?? []).map((it) => ({ value: it.contract_no, label: it.contract_no })),
    [picks?.npe],
  )
  const contractMeta = useMemo<Record<string, { supplier: string; date: string; ref: string; type: string }>>(() => {
    const m: Record<string, { supplier: string; date: string; ref: string; type: string }> = {}
    for (const it of picks?.p ?? [])
      m[it.contract_no] = { supplier: it.supplier_name ?? '', date: fmtDate(it.contract_date), ref: it.npe_ref ?? '', type: it.contract_type ?? '' }
    for (const it of picks?.npe ?? [])
      m[it.contract_no] = { supplier: it.supplier_name ?? '', date: fmtDate(it.contract_date), ref: '', type: '' }
    return m
  }, [picks?.p, picks?.npe])

  // Inject giá trị đã chọn vào options nếu list chưa chứa (Autocomplete blank guard).
  const pSelected = useMemo(
    () =>
      pOptions.find((o) => o.value === pContractNo) ??
      (pContractNo ? { value: pContractNo, label: pContractNo } : null),
    [pOptions, pContractNo],
  )
  const npeSelected = useMemo(
    () =>
      npeOptions.find((o) => o.value === npeContractNo) ??
      (npeContractNo ? { value: npeContractNo, label: npeContractNo } : null),
    [npeOptions, npeContractNo],
  )

  const h = variant === 'p' ? (headerP as Record<string, unknown> | null | undefined) : (headerNPE as Record<string, unknown> | null | undefined)
  const s = (k: string): string => {
    const v = h?.[k]
    return v == null ? '' : String(v)
  }

  // Field công thức Access (P):
  //   Price/Diff = IIf(contract_type="Outright", outright_price, ptbf_diffs)
  //   Inter Ref  = IIf(Sub?="Y", contract_shipper_intermediary, "")
  //   Shipper    = IIf(Sub?="Y", shipper_name_short, "")
  const isSub = s('intermediary_text').toUpperCase() === 'Y'
  const isOutright = s('contract_type').toLowerCase() === 'outright'

  return (
    <Card
      variant="outlined"
      sx={{
        flexShrink: 0,
        borderRadius: 0,
        border: 'none',
        boxShadow: 'none',
        // Nền tint nhạt + gạch dưới → tách header khỏi bảng trắng khi scroll (field trắng nổi trên nền này).
        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.05),
        borderBottom: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
      }}
    >
      <CardContent sx={{ p: { xs: 0.5, lg: 0.5 }, '&:last-child': { pb: { xs: 0.5, lg: 0.5 } } }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              md: 'repeat(3, minmax(0, 1fr))',
              lg: 'repeat(5, minmax(0, 1fr))',
            },
            gridAutoFlow: 'row',
            columnGap: { xs: 1.5, lg: 1 },
            rowGap: { xs: 0.6, lg: 0.15 },
            alignItems: 'center',
          }}
        >
          {/* ---- Hàng 1: Contract Type + Contract List (liên động) ---- */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, lg: 0.25 }, minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: '0.72rem', lg: '0.66rem' }, color: 'primary.main', fontWeight: 700, width: { xs: 118, lg: 84 }, flexShrink: 0, lineHeight: 1.2 }}>
              ChooseType
            </Typography>
            <RadioGroup
              row
              value={typeSelected.value}
              onChange={(_, v) => onSelectType(v as ContractVariant)}
              sx={{ flex: 1, minWidth: 0, gap: { xs: 0.5, lg: 1 }, flexWrap: 'nowrap' }}
            >
              {TYPE_OPTIONS.map((o) => (
                <FormControlLabel
                  key={o.value}
                  value={o.value}
                  control={<Radio size="small" sx={{ p: 0.25 }} />}
                  label={o.label}
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: { xs: '0.72rem', lg: '0.68rem' },
                      fontWeight: 700,
                      color: 'primary.main',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, lg: 0.25 }, minWidth: 0, gridColumn: { lg: 'span 2' } }}>
            <Typography sx={{ fontSize: { xs: '0.72rem', lg: '0.66rem' }, color: 'primary.main', fontWeight: 700, width: { xs: 118, lg: 84 }, flexShrink: 0, lineHeight: 1.2 }}>
              ChooseContract
            </Typography>
            <Autocomplete<PickOption, false, false, false>
              size="small"
              options={variant === 'p' ? pOptions : npeOptions}
              loading={picksLoading}
              value={variant === 'p' ? pSelected : npeSelected}
              isOptionEqualToValue={(o, v) => o.value === v.value}
              onChange={(_, opt) => (variant === 'p' ? onPickP : onPickNPE)(opt?.value ?? null)}
              getOptionLabel={(o) => o.label || ''}
              filterOptions={(opts, state) => {
                const q = state.inputValue.toLowerCase().trim()
                if (!q) return opts
                return opts.filter((o) => {
                  const m = contractMeta[o.value]
                  return [o.value, m?.supplier, m?.ref, m?.type].filter(Boolean).join(' ').toLowerCase().includes(q)
                })
              }}
              // Mỗi option: contract_no (đậm) + dòng phụ Supplier · Date (· NPE ref) — copy PO No BatchReport.
              renderOption={(props, option) => {
                const m = contractMeta[option.value]
                const { key, ...optionProps } = props as typeof props & { key?: string | number }
                const sub = m ? [m.supplier, m.type, m.date, m.ref].filter(Boolean).join(' · ') : ''
                return (
                  <Box component="li" key={key} {...optionProps} sx={{ display: 'block !important', py: 0.5 }}>
                    <Typography sx={{ fontSize: '0.8125rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.25 }}>
                      {option.label}
                    </Typography>
                    {sub ? (
                      <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2 }}>{sub}</Typography>
                    ) : null}
                  </Box>
                )
              }}
              slotProps={{ paper: { sx: { minWidth: 260 } } }}
              sx={pickerSx}
              renderInput={(params) => (
                <TextField {...params} placeholder="Choose Contract" sx={{ '& .MuiInputBase-input': { fontWeight: 700 } }} />
              )}
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.75, lg: 0.25 }, minWidth: 0 }}>
            <Typography sx={{ fontSize: { xs: '0.72rem', lg: '0.66rem' }, color: 'primary.main', fontWeight: 700, width: { xs: 118, lg: 84 }, flexShrink: 0, lineHeight: 1.2 }}>
              ChoosePage
            </Typography>
            <RadioGroup
              row
              value={modeSelected?.value ?? ''}
              onChange={(_, v) => onModeChange(v)}
              sx={{ flex: 1, minWidth: 0, gap: { xs: 0.5, lg: 1 }, flexWrap: 'nowrap' }}
            >
              {modeOptions.map((o) => (
                <FormControlLabel
                  key={o.value}
                  value={o.value}
                  control={<Radio size="small" sx={{ p: 0.25 }} />}
                  label={o.label}
                  sx={{
                    m: 0,
                    '& .MuiFormControlLabel-label': {
                      fontSize: { xs: '0.72rem', lg: '0.68rem' },
                      fontWeight: 700,
                      color: 'primary.main',
                      whiteSpace: 'nowrap',
                    },
                  }}
                />
              ))}
            </RadioGroup>
          </Box>

          {variant === 'p' ? (
            <>
              {/* Lưới lg pin cứng (row1 = combobox; data row2–6):
                  Cột1: Supplier·ContractType·DeliveryLatest·Received·TobeReceived
                  Cột2: Quantity·Price/Diffs·PriceUnit·Fixed·TobeFixed
                  Cột3: Month·Grade·Certification·TotalLot·LotFixed
                  Cột4–5: field còn lại. */}
              {/* Cột 1 */}
              <HField gridColumn={{ lg: '1' }} gridRow={{ lg: '2' }} label="Supplier" parts={[{ text: s('supplier_name'), bold: true }]} />
              <HField gridColumn={{ lg: '1' }} gridRow={{ lg: '3' }} label="Contract Type" parts={[{ text: s('contract_type') }]} />
              <HField gridColumn={{ lg: '1' }} gridRow={{ lg: '4' }} label="Delivery Latest" parts={[{ text: fmtDate(h?.['latest_delivery']) }]} />
              <HField gridColumn={{ lg: '1' }} gridRow={{ lg: '5' }} label="Received" parts={[{ text: fmtStd(h?.['received']), align: 'right' }]} />
              <HField gridColumn={{ lg: '1' }} gridRow={{ lg: '6' }} label="To be Received" parts={[{ text: fmtStd(h?.['to_be_received']), align: 'right', red: true }]} />
              {/* Cột 2 */}
              <HField gridColumn={{ lg: '2' }} gridRow={{ lg: '2' }} label="Quantity" parts={[{ text: fmtStd(h?.['quantity_kg']), align: 'right' }]} />
              <HField gridColumn={{ lg: '2' }} gridRow={{ lg: '3' }} label="Price/Diff" parts={[{ text: fmtStd(isOutright ? h?.['outright_price'] : h?.['ptbf_diffs']), align: 'right' }]} />
              <HField gridColumn={{ lg: '2' }} gridRow={{ lg: '4' }} label="Price Unit" parts={[{ text: s('price_unit') }]} />
              <HField gridColumn={{ lg: '2' }} gridRow={{ lg: '5' }} label="Fixed" parts={[{ text: fmtStd(h?.['kg_fixed']), align: 'right' }]} />
              <HField gridColumn={{ lg: '2' }} gridRow={{ lg: '6' }} label="To be Fixed" parts={[{ text: fmtStd(h?.['kg_to_fix']), align: 'right', red: true }]} />
              {/* Cột 3 */}
              <HField gridColumn={{ lg: '3' }} gridRow={{ lg: '2' }} label="Month" parts={[{ text: s('futures_month') }]} />
              <HField gridColumn={{ lg: '3' }} gridRow={{ lg: '3' }} label="Grade" parts={[{ text: s('quality_code') }]} />
              <HField gridColumn={{ lg: '3' }} gridRow={{ lg: '4' }} label="Certification" parts={[{ text: s('certification') }]} />
              <HField gridColumn={{ lg: '3' }} gridRow={{ lg: '5' }} label="Total Lot" parts={[{ text: fmtStd(h?.['total_lot']), align: 'right' }]} />
              <HField gridColumn={{ lg: '3' }} gridRow={{ lg: '6' }} label="Lot Fixed" parts={[{ text: fmtStd(h?.['lot_fixed']), align: 'right' }]} />
              {/* Cột 4 — Delivery cao 2 dòng */}
              <HField gridColumn={{ lg: '4' }} gridRow={{ lg: '2' }} label="Market Level" parts={[{ text: fmtStd(h?.['avg_futures_price_real']), align: 'right' }]} />
              <HField gridColumn={{ lg: '4' }} gridRow={{ lg: '3' }} label="Price/Diffs" parts={[{ text: fmtStd(h?.['price_diffs_2']), align: 'right' }]} />
              <HField gridColumn={{ lg: '4' }} gridRow={{ lg: '4' }} label="Final Price" parts={[{ text: fmtStd(h?.['avg_price2_real']), align: 'right' }]} />
              <HField gridColumn={{ lg: '4' }} gridRow={{ lg: '5 / span 2' }} multiline label="Delivery" parts={[{ text: s('delivery_place'), bold: true }]} />
              {/* Cột 5 */}
              <HField gridColumn={{ lg: '5' }} gridRow={{ lg: '2' }} label="Contract Date" parts={[{ text: fmtDate(h?.['contract_date']) }]} />
              <HField gridColumn={{ lg: '5' }} gridRow={{ lg: '3' }} label="Inter Ref" parts={[{ text: isSub ? s('contract_shipper_intermediary') : '' }]} />
              <HField gridColumn={{ lg: '5' }} gridRow={{ lg: '4' }} label="Shipper" parts={[{ text: isSub ? s('shipper_name_short') : '' }]} />
              <HField gridColumn={{ lg: '5' }} gridRow={{ lg: '5' }} label={'Fixed FX\n(on Contract)'} parts={[{ text: fmtStd(h?.['fixed_fx']) }]} />
              <HField gridColumn={{ lg: '5' }} gridRow={{ lg: '6' }} label="Sub?" parts={[{ text: s('intermediary_text') }]} />
            </>
          ) : (
            <>
              {/* Lưới lg pin cứng theo style P (row1 = combobox; data row2–6). 21 field NPE:
                  rows 2–4 = thông tin; rows 5–6 = cụm Received/Advanced/Fixed + To be *. */}
              {/* Cột 1 — định danh */}
              <HField gridColumn={{ lg: '1' }} gridRow={{ lg: '2' }} label="Contract Date" parts={[{ text: fmtDate(h?.['contract_date']), bold: true }]} />
              <HField gridColumn={{ lg: '1' }} gridRow={{ lg: '3' }} label="Supplier Code" parts={[{ text: s('cp_code') }]} />
              <HField gridColumn={{ lg: '1' }} gridRow={{ lg: '4' }} label="Supplier Name" parts={[{ text: s('cp_name') || s('supplier_name'), bold: true }]} />
              <HField gridColumn={{ lg: '1' }} gridRow={{ lg: '5' }} label="Received" parts={[{ text: fmtStd(h?.['received']), align: 'right' }]} />
              <HField gridColumn={{ lg: '1' }} gridRow={{ lg: '6' }} label="To be Received" parts={[{ text: fmtStd(h?.['to_be_receive']), align: 'right', red: true }]} />
              {/* Cột 2 — số lượng / tiền */}
              <HField gridColumn={{ lg: '2' }} gridRow={{ lg: '2' }} label="Quantity" parts={[{ text: fmtStd(h?.['quantity_kg']), align: 'right' }]} />
              <HField gridColumn={{ lg: '2' }} gridRow={{ lg: '3' }} label="No Bag" parts={[{ text: fmtStd(h?.['no_bag']), align: 'right' }]} />
              <HField gridColumn={{ lg: '2' }} gridRow={{ lg: '4' }} label="Paid VNĐ" parts={[{ text: fmtStd(h?.['paid_vnd']), align: 'right' }]} />
              <HField gridColumn={{ lg: '2' }} gridRow={{ lg: '5' }} label="Advanced" parts={[{ text: fmtStd(h?.['advanced']), align: 'right' }]} />
              <HField gridColumn={{ lg: '2' }} gridRow={{ lg: '6' }} label="To be Advanced" parts={[{ text: fmtStd(h?.['to_be_advance']), align: 'right', red: true }]} />
              {/* Cột 3 — chất lượng */}
              <HField gridColumn={{ lg: '3' }} gridRow={{ lg: '2' }} label="Grade" parts={[{ text: s('quality_code') }]} />
              <HField gridColumn={{ lg: '3' }} gridRow={{ lg: '3' }} label="Certification" parts={[{ text: s('certification') }]} />
              <HField gridColumn={{ lg: '3' }} gridRow={{ lg: '4' }} label="Package" parts={[{ text: s('package_name') }]} />
              <HField gridColumn={{ lg: '3' }} gridRow={{ lg: '5' }} label="Fixed" parts={[{ text: fmtStd(h?.['fixed']), align: 'right' }]} />
              <HField gridColumn={{ lg: '3' }} gridRow={{ lg: '6' }} label="To be Fixed" parts={[{ text: fmtStd(h?.['to_be_fix']), align: 'right', red: true }]} />
              {/* Cột 4 — giao hàng */}
              <HField gridColumn={{ lg: '4' }} gridRow={{ lg: '2' }} label="Delivery Latest" parts={[{ text: fmtDate(h?.['delivery_time']) }]} />
              <HField gridColumn={{ lg: '4' }} gridRow={{ lg: '3 / span 2' }} multiline label="Delivery Place" parts={[{ text: s('delivery_place') }]} />
              <HField gridColumn={{ lg: '4' }} gridRow={{ lg: '5 / span 2' }} multiline label="Interest Rate" parts={[{ text: s('interest_rate_note') }]} />
              {/* Cột 5 — tỷ lệ + Note (2 dòng) */}
              <HField gridColumn={{ lg: '5' }} gridRow={{ lg: '2' }} label="Stoploss Rate" parts={[{ text: fmtPercent(h?.['stoploss_rate']), align: 'right' }]} />
              <HField gridColumn={{ lg: '5' }} gridRow={{ lg: '3' }} label="Provi. Market Rate" parts={[{ text: fmtPercent(h?.['provisional_market_rate']), align: 'right' }]} />
              <HField gridColumn={{ lg: '5' }} gridRow={{ lg: '4 / span 2' }} multiline label="Note" parts={[{ text: s('note') }]} />
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}
